import { useState, useEffect, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { usePostHog } from '@/contexts/PostHogProvider';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
  passwordRecoveryMode: boolean;
  resetPasswordRecoveryMode: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecoveryMode, setPasswordRecoveryMode] = useState(false);
  const { posthog, isInitialized } = usePostHog();

  const trackUserSignUp = (method: string) => {
    if (isInitialized && posthog) posthog.capture('user_sign_up', { method });
  };
  const trackUserLogin = (method: string) => {
    if (isInitialized && posthog) posthog.capture('user_login', { method });
  };
  const trackUserLogout = () => {
    if (isInitialized && posthog) posthog.capture('user_logout');
  };
  const trackAuthError = (type: string, message: string) => {
    if (isInitialized && posthog) posthog.capture('auth_error', { type, message });
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (event === 'PASSWORD_RECOVERY') {
          setPasswordRecoveryMode(true);
        }
        
        // Set OAuth return flag when user signs in via OAuth
        if (event === 'SIGNED_IN' && session?.user) {
          // Check if this is an OAuth sign-in (Google)
          const isOAuth = session.user.app_metadata?.provider === 'google';
          if (isOAuth) {
            sessionStorage.setItem('oauth_return', 'true');
          }
        }
      }
    );

    // Check for existing session
    const bootstrapSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        setUser(session?.user ?? null);
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message.includes('Navigator LockManager lock')) {
          console.warn('[useAuth] Session lock timeout during bootstrap, waiting for auth state change');
        } else {
          console.warn('[useAuth] Failed to load initial session:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    bootstrapSession();

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      trackAuthError('sign_in_failed', error.message);
    } else {
      trackUserLogin('email');
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    
    if (error) {
      trackAuthError('sign_up_failed', error.message);
    } else {
      trackUserSignUp('email');
    }
    
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
        queryParams: {
          prompt: 'select_account'
        }
      }
    });
    
    if (error) {
      trackAuthError('google_sign_in_failed', error.message);
    } else {
      trackUserLogin('google');
    }
    
    return { error };
  };

  const signOut = async () => {
    trackUserLogout();
    // Clear state immediately to prevent redirect race conditions
    setUser(null);
    setSession(null);
    await supabase.auth.signOut({ scope: 'local' });
  };

  const resetPasswordRecoveryMode = () => setPasswordRecoveryMode(false);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      loading,
      passwordRecoveryMode,
      resetPasswordRecoveryMode,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Provider + consumer hook colocation is the canonical React context pattern;
// React Fast Refresh's per-file rule is a false positive here.
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
